/**
 * Contrato de leitura dos alertas em tempo real do dashboard (épico dashboard-alertas).
 *
 * Fonte: evento SignalR `AlertsChanged` no hub `/hubs/station-state` (o MESMO do semáforo).
 * Não há endpoint REST — o snapshot inicial vem no `OnConnectedAsync` (Clients.Caller recebe
 * `AlertsChanged` com os alertas vigentes). Feature somente leitura, sem texto localizado:
 * `type` é chave que o front traduz (mesma postura do `StateChanged`).
 *
 * Os campos chegam em camelCase; `normalizeAlerts` também aceita PascalCase por defesa.
 */

/** Tipo de alerta (AlertTypeEnum). Chave — o front localiza. */
export type AlertTypeKey = "IdleWithMaterial" | "ExtraVagaOccupied";

export interface Alert {
  /** "IdleWithMaterial" (estação em Ausência > 5 min) | "ExtraVagaOccupied" (vaga Extra ocupada > 10 min). */
  type: AlertTypeKey | string;
  /** Vaga do alerta de vaga Extra (AC-3); null no alerta de estação (AC-2). */
  vagaKey: string | null;
  /** Instante em que a condição passou a valer (relógio da borda; exibição). */
  since: string | null;
  /** Há quanto a condição se sustenta, em segundos (relógio de servidor no `generatedAt`). */
  elapsedSeconds: number;
}

export interface AlertsPayload {
  /** UTC do servidor — âncora para o cliente contar o elapsed localmente entre pushes. */
  generatedAt: string | null;
  /** Alertas ativos; vazio = nenhum alerta. */
  alerts: Alert[];
}

const numOr0 = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pick = (obj: any, ...keys: string[]): any => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

/** Normaliza o payload do `AlertsChanged` (tolerante a casing). `null` se inaproveitável. */
export const normalizeAlerts = (raw: any): AlertsPayload | null => {
  if (!raw || typeof raw !== "object") return null;
  const alertsRaw = pick(raw, "alerts", "Alerts");
  return {
    generatedAt: pick(raw, "generatedAt", "GeneratedAt") ?? null,
    alerts: (Array.isArray(alertsRaw) ? alertsRaw : []).map((a: any) => ({
      type: pick(a, "type", "Type") ?? "",
      vagaKey: pick(a, "vagaKey", "VagaKey") ?? null,
      since: pick(a, "since", "Since") ?? null,
      elapsedSeconds: numOr0(pick(a, "elapsedSeconds", "ElapsedSeconds")),
    })),
  };
};
